import React, { useMemo, useState } from 'react';
import { Btn, Msg, Section, fmtDate, panelPost } from '../kit';
import { Check, Modal, inputCls } from '../email/emailKit';
import { AdminLink } from '../adminNav';
import { PreviewFrame, Radio, Toggle, useDraft } from '../homepage/shared';
import OnboardingModal from '../../OnboardingModal';
import {
  type OnboardingCopy, type OnboardingPublicConfig, fillPicksHeading, resolveOnboardingCopy,
} from '../../../types/onboarding';
import {
  type CardDepth, CheckRow, type ConfigData, type Flow, type Note, S, ago, fmt, yesNo,
} from './shared';

/**
 * Admin → Onboarding → Settings: the switches (Lock search for Explorers
 * among them), the dropdown's industries (shown / hidden / order), every
 * line of modal and landing copy with a live preview of the real modal, and
 * the one-click Go live / Roll back.
 *
 * Go live (onboarding/go_live) snapshots the current flow, the free-tier lock
 * and the sign-in plans prompt, then turns on flow v2 + the Explorer picks,
 * locks search for free accounts (optional) and switches the sign-in prompt
 * off (optional). Roll back restores exactly that snapshot.
 *
 * Whether Go live locks search is the saved Switches → "Lock search for
 * Explorers" (config.lock_search): the Go live card shows it and the request
 * leaves lock_search out, so the server uses the stored switch and the two
 * controls can never disagree. Only an older server that does not send the
 * switch gets the card's own "Lock search for free accounts" box (ticked by
 * default, as before), sent as lock_search.
 */

type CopyKey = keyof OnboardingCopy;

interface Draft {
  flow: Flow;
  explorer_picks: boolean;
  entry_modal: boolean;
  picks_card_depth: CardDepth;
  /** Null: the server does not store it yet (the switch is not offered). */
  lock_search: boolean | null;
  order: string[];
  hidden: string[];
  copy: Record<string, string>;
}

const OTHERS = 'Others';

/** Copy fields, grouped as the screens show them, with their length limits. */
const COPY_GROUPS: { title: string; fields: { key: CopyKey; label: string; max: number; long?: boolean; hint?: string }[] }[] = [
  {
    title: 'Step 1 — industry',
    fields: [
      { key: 'step1_title', label: 'Heading', max: 90 },
      { key: 'step1_sub', label: 'Subtitle', max: 200, long: true },
      { key: 'industry_label', label: 'Field label', max: 32 },
      { key: 'industry_placeholder', label: 'Dropdown placeholder', max: 32 },
    ],
  },
  {
    title: 'Step 2 — keyword',
    fields: [
      { key: 'step2_title', label: 'Heading', max: 90 },
      { key: 'step2_sub', label: 'Subtitle', max: 200, long: true },
      { key: 'keyword_label', label: 'Chips label', max: 32 },
      { key: 'no_chips_note', label: 'Note for an industry with no chips (e.g. Others)', max: 200, long: true },
    ],
  },
  {
    title: 'Buttons',
    fields: [
      { key: 'skip_label', label: 'Skip', max: 32 },
      { key: 'explore_cta', label: 'Explore', max: 32 },
    ],
  },
  {
    title: 'Cosmos landing (Explorers)',
    fields: [
      { key: 'picks_eyebrow', label: 'Eyebrow over a personal top 3', max: 32 },
      { key: 'picks_heading_kw', label: 'Heading: industry + keyword', max: 90, hint: '{industry} and {keyword}' },
      { key: 'picks_heading_industry', label: 'Heading: industry only', max: 90, hint: '{industry}' },
      { key: 'picks_eyebrow_random', label: 'Eyebrow over the 3 random cards', max: 32 },
      { key: 'picks_heading_random', label: 'Heading over the 3 random cards', max: 90, hint: 'blank = eyebrow only' },
    ],
  },
];

/** The 11 keys every v2 server knows; the rest only if the server lists them. */
const CORE_KEYS: CopyKey[] = [
  'step1_title', 'step1_sub', 'industry_placeholder', 'step2_title', 'step2_sub', 'skip_label', 'explore_cta',
  'no_chips_note', 'picks_heading_kw', 'picks_heading_industry', 'picks_heading_random',
];

const ordered = (names: string[], order: string[]): string[] => {
  const known = new Set(names);
  const head = order.filter(n => known.has(n) && n !== OTHERS);
  const rest = names.filter(n => !head.includes(n) && n !== OTHERS);
  return [...head, ...rest, ...(known.has(OTHERS) ? [OTHERS] : [])];
};

const draftFrom = (d: ConfigData): Draft => ({
  flow: d.config.flow,
  explorer_picks: d.config.explorer_picks,
  entry_modal: d.config.entry_modal,
  picks_card_depth: d.config.picks_card_depth,
  lock_search: d.config.lock_search,
  order: ordered(d.all_names, d.config.industry_order),
  hidden: [...d.config.hidden_industries],
  copy: Object.fromEntries(Object.entries(d.config.copy || {}).map(([k, v]) => [k, String(v ?? '')])),
});

const move = <T,>(list: T[], i: number, dir: -1 | 1): T[] => {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

const Settings: React.FC<{
  data: ConfigData; onSaved: (d: any) => void; onDirty: (v: boolean) => void; reload: () => void;
}> = ({ data, onSaved, onDirty, reload }) => {
  const { draft, edit, dirty, discard, settle } = useDraft<ConfigData, Draft>(data, draftFrom, onDirty);
  const [busy, setBusy] = useState<'' | 'save' | 'go_live' | 'roll_back'>('');
  const [msg, setMsg] = useState<Note>(null);
  const [confirm, setConfirm] = useState<'' | 'go_live' | 'roll_back'>('');
  // Older server only (no lock_search in its config): the Go live card's own box.
  const [lockSearch, setLockSearch] = useState(true);
  const [signinOff, setSigninOff] = useState(true);
  const [previewIndustry, setPreviewIndustry] = useState('');

  const cfg = data.config;
  const ck = data.checklist;
  const live = cfg.flow === 'v2';
  const snapshot = cfg._before_go_live;
  // The saved "Lock search for Explorers" switch decides what Go live does
  // with search; null = an older server without it (the card's box decides).
  const switchKnown = cfg.lock_search !== null;
  const goLiveLocks = switchKnown ? cfg.lock_search === true : lockSearch;

  // Copy keys this server accepts: the 11 core ones, plus any it lists in its
  // defaults or already stores. An unknown key would fail the whole save.
  const knownKeys = useMemo(() => {
    const k = new Set<string>(CORE_KEYS);
    Object.keys(data.raw?.defaults?.copy || data.raw?.default_copy || {}).forEach(x => k.add(x));
    Object.keys(cfg.copy || {}).forEach(x => k.add(x));
    return k;
  }, [data, cfg.copy]);

  const chipsOf = (name: string) => data.industries.find(i => i.name === name)?.chips || [];
  const visible = draft.order.filter(n => !draft.hidden.includes(n));
  const firstWithChips = visible.find(n => chipsOf(n).length) || visible[0] || '';
  const pIndustry = previewIndustry && visible.includes(previewIndustry) ? previewIndustry : firstWithChips;

  const effectiveCopy = useMemo(() => {
    const merged: Partial<OnboardingCopy> = { ...data.defaults };
    Object.entries(draft.copy).forEach(([k, v]) => { if (v.trim()) (merged as any)[k] = v; });
    return resolveOnboardingCopy(merged);
  }, [draft.copy, data.defaults]);

  const previewConfig: OnboardingPublicConfig = {
    flow: 'v2', explorer_picks: draft.explorer_picks, entry_modal: draft.entry_modal,
    config_version: 'preview',
    industries: visible.map(n => ({ name: n, chips: chipsOf(n), chips_source: chipsOf(n).length ? 'corpus' : 'none' })),
    copy: effectiveCopy,
  };

  const copyErrors = COPY_GROUPS.flatMap(g => g.fields)
    .filter(f => (draft.copy[f.key] || '').length > f.max)
    .map(f => f.label);

  const save = async () => {
    if (copyErrors.length) { setMsg({ kind: 'error', text: `Too long: ${copyErrors.join(', ')}.` }); return; }
    if (!visible.length) { setMsg({ kind: 'error', text: 'Show at least one industry.' }); return; }
    // Only what changed: config_set is a partial update, so a field nobody
    // touched here can never overwrite a newer value (Go live, another admin).
    const base = draftFrom(data);
    const patch: Record<string, any> = {};
    (['flow', 'explorer_picks', 'entry_modal', 'picks_card_depth', 'lock_search'] as const).forEach(k => {
      if (draft[k] !== base[k]) patch[k] = draft[k];
    });
    if (JSON.stringify(draft.hidden) !== JSON.stringify(base.hidden)) patch.hidden_industries = draft.hidden;
    if (JSON.stringify(draft.order) !== JSON.stringify(base.order)) {
      patch.industry_order = draft.order.filter(n => n !== OTHERS);
    }
    const copy: Record<string, string> = {};
    Object.entries(draft.copy).forEach(([k, v]) => {
      if (knownKeys.has(k) && v.trim() !== (base.copy[k] || '').trim()) copy[k] = v.trim();
    });
    if (Object.keys(copy).length) patch.copy = copy;
    if (!Object.keys(patch).length) { settle(); setMsg({ kind: 'ok', text: 'Nothing changed.' }); return; }
    setBusy('save'); setMsg(null);
    const { data: next, error } = await panelPost(S, 'config_set', patch);
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    settle();
    onSaved(next);
    setMsg({
      kind: 'ok',
      text: Object.keys(patch).length === 1 && 'lock_search' in patch
        ? 'Saved. Explorers get it on their next page load (the public config is cached for a minute).'
        : 'Saved. New sign-ups get it on their next page load (the public config is cached for a minute).',
    });
  };

  const goLive = async () => {
    setConfirm(''); setBusy('go_live'); setMsg(null);
    // With the switch on the server, lock_search stays out of the body: the
    // server uses (and keeps) the stored switch.
    const body: Record<string, any> = { signin_modal_off: signinOff };
    if (!switchKnown) body.lock_search = lockSearch;
    const { error } = await panelPost(S, 'go_live', body);
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: `Go live failed: ${error}` }); return; }
    settle();
    reload();
    setMsg({ kind: 'ok', text: 'Live. New Explorers now get the two-step onboarding and land on their 3 picks.' });
  };

  const rollBack = async () => {
    setConfirm(''); setBusy('roll_back'); setMsg(null);
    const { error } = await panelPost(S, 'roll_back', {});
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: `Roll back failed: ${error}` }); return; }
    settle();
    reload();
    setMsg({ kind: 'ok', text: 'Rolled back to the settings from before Go live.' });
  };

  const setCopy = (k: string, v: string) => edit(d => ({ ...d, copy: { ...d.copy, [k]: v } }));
  const lock = ck.lock;

  return (
    <div className="flex flex-col gap-4">
      {/* ---- Status + Go live / Roll back ---------------------------------- */}
      <Section title="Go live"
        right={live
          ? <span className="text-xs font-bold text-green-800 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">Live · flow v2</span>
          : <span className="text-xs font-bold text-grayscale-1200 bg-grayscale-200 rounded-full px-2.5 py-0.5">Off · flow v1 (today's onboarding)</span>}>
        <p className="text-[13px] text-grayscale-1200 max-w-3xl">
          {live
            ? 'New sign-ups get the two-step onboarding (industry, then one keyword chip). Explorers land on 3 picked technologies and the upgrade block, with no plans sheet on entry.'
            : 'Everyone still gets the current onboarding (chips, free-text keyword, preview cards, then the plans sheet). Review Industries & keywords and Cosmos picks, then press Go live.'}
        </p>

        <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 mb-1">Current values</div>
            <CheckRow label="Onboarding flow" value={cfg.flow === 'v2' ? 'v2 (new)' : 'v1 (today)'} ok={cfg.flow === 'v2'} />
            <CheckRow label="Explorers land on 3 picks" value={yesNo(cfg.explorer_picks)} ok={cfg.explorer_picks} />
            {cfg.lock_search !== null && (
              <CheckRow label="Search box locked for Explorers" value={yesNo(cfg.lock_search)} ok={cfg.lock_search}
                hint="Switches → Lock search for Explorers" />
            )}
            <CheckRow label="Plans sheet after onboarding" value={yesNo(cfg.entry_modal)} ok={!cfg.entry_modal} />
            <CheckRow label="Search allowed on free" value={yesNo(lock.can_search, 'Yes', 'No — locked')}
              ok={lock.can_search === null ? null : !lock.can_search}
              hint="Billing → Promotions & trials → Free-tier lock" />
            <CheckRow label="Free-tier lock" value={yesNo(lock.enabled)} ok={lock.enabled}
              hint="Off = no locks at all, so Explorers keep the full Cosmos page" />
            <CheckRow label="Results shown to a locked search" value={fmt(lock.preview_results)}
              ok={lock.preview_results === null ? null : lock.preview_results >= 3} hint="Go live raises it to 3" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 mb-1 md:invisible">&nbsp;</div>
            <CheckRow label="Sign-in plans prompt" value={yesNo(ck.signin_modal_enabled)}
              ok={ck.signin_modal_enabled === null ? null : !ck.signin_modal_enabled}
              hint="Billing → Upgrade prompts → Sign-in upgrade sheet" />
            <CheckRow label="Upgrade block under the 3 picks" value={yesNo(ck.picks_block_enabled)}
              ok={ck.picks_block_enabled} hint="Billing → Upgrade prompts → Cosmos picks block" />
            <CheckRow label="Picks last built" value={ck.picks_built_at ? ago(ck.picks_built_at) : 'never'}
              ok={ck.picks_built_at ? true : false} />
            <CheckRow label="Thin lists (under 3 matches)" value={fmt(ck.thin_count)}
              ok={ck.thin_count === null ? null : ck.thin_count === 0} hint="Cosmos picks tab: pin, or change the chip" />
            <CheckRow label="Picked ids gone from the corpus" value={fmt(ck.ids_missing)}
              ok={ck.ids_missing === null ? null : ck.ids_missing === 0} hint="Served picks skip them; Refresh rebuilds" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-2 border-t border-grayscale-200">
          {!live && (
            <>
              {switchKnown ? (
                <span className="text-xs text-grayscale-1200"
                  title="Change it in Switches below → Lock search for Explorers, then Save">
                  Lock search for free accounts: <b>{goLiveLocks ? 'on' : 'off'}</b>
                  <span className="text-grayscale-1100"> (Switches → Lock search for Explorers)</span>
                </span>
              ) : (
                <Check checked={lockSearch} onChange={setLockSearch}>Lock search for free accounts</Check>
              )}
              <Check checked={signinOff} onChange={setSigninOff}>Turn the sign-in plans prompt off</Check>
              <Btn kind="primary" onClick={() => setConfirm('go_live')} disabled={!!busy || dirty}
                title={dirty ? 'Save or discard the changes below first' : undefined}>
                {busy === 'go_live' ? 'Going live…' : 'Go live'}
              </Btn>
            </>
          )}
          {(live || snapshot) && (
            <Btn kind="danger" onClick={() => setConfirm('roll_back')} disabled={!!busy || dirty}
              title={dirty ? 'Save or discard the changes below first' : undefined}>
              {busy === 'roll_back' ? 'Rolling back…' : 'Roll back'}
            </Btn>
          )}
          {dirty && <span className="text-[11px] text-amber-700">Save or discard the changes below first.</span>}
          <span className="text-[11px] text-grayscale-1100">
            <AdminLink to={{ group: 'billing', tab: 'promos' }}>Free-tier lock</AdminLink>
            {' · '}
            <AdminLink to={{ group: 'billing', tab: 'prompts' }}>Upgrade prompts</AdminLink>
          </span>
        </div>
      </Section>

      {confirm === 'go_live' && (
        <Modal title="Go live with the new onboarding?" onClose={() => setConfirm('')}
          footer={<>
            <Btn onClick={() => setConfirm('')}>Cancel</Btn>
            <Btn kind="primary" onClick={goLive}>Go live</Btn>
          </>}>
          <p>This changes what every new sign-up and every Explorer sees, straight away:</p>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            <li>Onboarding becomes the two steps (industry, then one keyword chip). No preview cards, no plans sheet after it.</li>
            <li>Explorers land on their 3 picks and the upgrade block instead of Popular technologies.</li>
            {goLiveLocks
              ? <li>Search is locked for free accounts (Explorers see a locked search bar; a click opens the plans sheet). Locked searches show at most {Math.max(3, lock.preview_results || 0)} results.</li>
              : <li>Search stays as it is now ({lock.can_search === false ? 'already locked' : 'open'}).</li>}
            {signinOff
              ? <li>The plans sheet after sign-in is switched off.</li>
              : <li>The plans sheet after sign-in stays {ck.signin_modal_enabled === false ? 'off' : 'on'}.</li>}
          </ul>
          <p className="text-xs text-grayscale-1100">Roll back restores every one of these to what it is now.</p>
        </Modal>
      )}
      {confirm === 'roll_back' && (
        <Modal title="Roll back to the previous onboarding?" onClose={() => setConfirm('')}
          footer={<>
            <Btn onClick={() => setConfirm('')}>Cancel</Btn>
            <Btn kind="danger" onClick={rollBack}>Roll back</Btn>
          </>}>
          {snapshot ? (
            <>
              <p>Puts back the values saved when Go live was pressed{snapshot.at ? ` (${fmtDate(snapshot.at)}${snapshot.by ? `, ${snapshot.by}` : ''})` : ''}:</p>
              <ul className="list-disc pl-5 flex flex-col gap-1">
                {'flow' in snapshot && <li>Onboarding flow: {String(snapshot.flow)}</li>}
                {'explorer_picks' in snapshot && <li>Explorers land on 3 picks: {snapshot.explorer_picks ? 'on' : 'off'}</li>}
                {'entry_modal' in snapshot && <li>Plans sheet after onboarding: {snapshot.entry_modal ? 'on' : 'off'}</li>}
                {typeof snapshot.lock_search === 'boolean' &&
                  <li>Lock search for Explorers: {snapshot.lock_search ? 'on' : 'off'}</li>}
                {snapshot.lock && typeof snapshot.lock === 'object' && (
                  <li>Search allowed on free: {snapshot.lock.can_search ? 'yes' : 'no'}
                    {snapshot.lock.preview_results !== undefined ? ` · locked searches show ${snapshot.lock.preview_results}` : ''}</li>
                )}
                {typeof snapshot.signin_modal_enabled === 'boolean' &&
                  <li>Sign-in plans prompt: {snapshot.signin_modal_enabled ? 'on' : 'off'}</li>}
                {snapshot.signin_modal && typeof snapshot.signin_modal === 'object' && 'enabled' in snapshot.signin_modal &&
                  <li>Sign-in plans prompt: {snapshot.signin_modal.enabled ? 'on' : 'off'}</li>}
              </ul>
            </>
          ) : (
            <p>No Go live snapshot is stored, so only the flow goes back to v1 (today's onboarding). The search lock and the sign-in prompt stay as they are.</p>
          )}
          <p className="text-xs text-grayscale-1100">
            Only what Go live changed is put back; picks, keywords and copy are kept, ready for the next Go live.
          </p>
        </Modal>
      )}

      {/* ---- Switches ------------------------------------------------------ */}
      <Section title="Switches">
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="text-xs text-grayscale-1100 w-40">Onboarding flow</span>
            <Radio name="onb-flow" checked={draft.flow === 'v1'} onChange={() => edit(d => ({ ...d, flow: 'v1' }))}>
              v1 — today's modal (preview cards, then the plans sheet)
            </Radio>
            <Radio name="onb-flow" checked={draft.flow === 'v2'} onChange={() => edit(d => ({ ...d, flow: 'v2' }))}>
              v2 — two steps, 3 picks
            </Radio>
          </div>
          <div className="text-[11px] text-grayscale-1100 -mt-2 ml-0 md:ml-[11.25rem]">
            {switchKnown
              ? 'Prefer Go live / Roll back above: they also set the sign-in prompt and keep a note Roll back restores. Switching the flow here keeps the search lock in step: v2 locks search for free accounts when Lock search for Explorers is on, v1 puts back what onboarding locked.'
              : 'Prefer Go live / Roll back above: they also set the search lock and the sign-in prompt. Switching the flow here changes only the flow.'}
          </div>
          <label className="flex items-center gap-3">
            <span className="text-xs text-grayscale-1100 w-40">Explorers land on 3 picks</span>
            <Toggle on={draft.explorer_picks} onChange={v => edit(d => ({ ...d, explorer_picks: v }))} label="Explorers land on 3 picks" />
            <span className="text-xs text-grayscale-1100">Off: v2 onboarding, but Explorers see Popular technologies as today.</span>
          </label>
          {draft.lock_search === null ? (
            <div className="flex items-center gap-3">
              <span className="text-xs text-grayscale-1100 w-40">Lock search for Explorers</span>
              <span className="text-xs text-grayscale-1100">
                Not on this server yet: the free-tier lock's Search setting decides (<AdminLink to={{ group: 'billing', tab: 'promos' }}>Free-tier lock</AdminLink>).
              </span>
            </div>
          ) : (
            <label className="flex items-center gap-3">
              <span className="text-xs text-grayscale-1100 w-40">Lock search for Explorers</span>
              <Toggle on={draft.lock_search} onChange={v => edit(d => ({ ...d, lock_search: v }))} label="Lock search for Explorers" />
              <span className="text-xs text-grayscale-1100">
                With the new flow (v2) on, free accounts get a locked search box (clicking or typing in it opens the plans sheet) and the server locks their search. Go live uses this switch.
              </span>
            </label>
          )}
          <label className="flex items-center gap-3">
            <span className="text-xs text-grayscale-1100 w-40">Plans sheet after onboarding</span>
            <Toggle on={draft.entry_modal} onChange={v => edit(d => ({ ...d, entry_modal: v }))} label="Plans sheet after onboarding" />
            <span className="text-xs text-grayscale-1100">Off by default: no modal on entry.</span>
          </label>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="text-xs text-grayscale-1100 w-40">The 3 picked cards</span>
            <Radio name="onb-depth" checked={draft.picks_card_depth === 'full_except_contact'}
              onChange={() => edit(d => ({ ...d, picks_card_depth: 'full_except_contact' }))}>
              Show all their information (Contact University still opens the plans sheet)
            </Radio>
            <Radio name="onb-depth" checked={draft.picks_card_depth === 'as_today'}
              onChange={() => edit(d => ({ ...d, picks_card_depth: 'as_today' }))}>
              As any free card today (some sections locked)
            </Radio>
          </div>
        </div>
      </Section>

      {/* ---- Industries ---------------------------------------------------- */}
      <Section title={`Industries in the dropdown — ${visible.length} shown`}
        right={<span className="text-[11px] text-grayscale-1100">“Others” is always last and gives 3 random cards.</span>}>
        <div className="flex flex-col gap-1">
          {draft.order.map((n, i) => {
            const hidden = draft.hidden.includes(n);
            const isOthers = n === OTHERS;
            const chips = chipsOf(n);
            return (
              <div key={n} className={`flex items-center gap-2 rounded-lg border border-grayscale-300 px-2 py-1.5 ${hidden ? 'opacity-50' : ''}`}>
                <span className="text-xs text-grayscale-1100 w-5 text-right shrink-0">{i + 1}</span>
                <span className="flex-1 min-w-0 text-sm text-grayscale-1300 truncate" title={n}>{n}</span>
                <span className="text-[11px] text-grayscale-1100 hidden sm:inline">
                  {isOthers ? 'no chips · random' : `${chips.length} chip${chips.length === 1 ? '' : 's'}`}
                </span>
                <label className="flex items-center gap-1.5 text-xs text-grayscale-1200 cursor-pointer">
                  <input type="checkbox" className="accent-primary-500" checked={!hidden}
                    onChange={e => edit(d => ({
                      ...d, hidden: e.target.checked ? d.hidden.filter(x => x !== n) : [...d.hidden, n],
                    }))} />
                  Shown
                </label>
                <Btn small onClick={() => edit(d => ({ ...d, order: move(d.order, i, -1) }))}
                  disabled={isOthers || i === 0} title="Move up">↑</Btn>
                <Btn small onClick={() => edit(d => ({ ...d, order: move(d.order, i, 1) }))}
                  disabled={isOthers || i >= draft.order.length - 2 || draft.order[i + 1] === OTHERS} title="Move down">↓</Btn>
              </div>
            );
          })}
          {!draft.order.length && <div className="text-sm text-grayscale-1100">No industries came back from the server.</div>}
        </div>
      </Section>

      {/* ---- Copy + preview ----------------------------------------------- */}
      <Section title="Wording">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="flex flex-col gap-4 min-w-0">
            <div className="text-[11px] text-grayscale-1100">A blank field uses the default shown in grey.</div>
            {COPY_GROUPS.map(g => (
              <div key={g.title} className="flex flex-col gap-2">
                <div className="text-xs font-bold text-grayscale-1300">{g.title}</div>
                {g.fields.filter(f => knownKeys.has(f.key)).map(f => {
                  const v = draft.copy[f.key] || '';
                  const over = v.length > f.max;
                  return (
                    <label key={f.key} className="flex flex-col gap-1 text-xs text-grayscale-1100">
                      <span className="flex justify-between gap-2">
                        <span>{f.label}{f.hint ? <span className="text-grayscale-900"> · {f.hint}</span> : null}</span>
                        <span className={over ? 'text-red-600' : ''}>{v.length}/{f.max}</span>
                      </span>
                      {f.long ? (
                        <textarea value={v} rows={2} placeholder={(data.defaults as any)[f.key] || ''}
                          onChange={e => setCopy(f.key, e.target.value)}
                          className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
                      ) : (
                        <input value={v} placeholder={(data.defaults as any)[f.key] || ''}
                          onChange={e => setCopy(f.key, e.target.value)} className={inputCls} />
                      )}
                    </label>
                  );
                })}
                {g.fields.some(f => !knownKeys.has(f.key)) && (
                  <div className="text-[11px] text-grayscale-1100">
                    {g.fields.filter(f => !knownKeys.has(f.key)).map(f => f.label).join(', ')}: built-in wording (this server does not store it yet).
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-3 min-w-0">
            <div className="flex items-center gap-2 text-xs text-grayscale-1100">
              <span>Step 2 preview for</span>
              <select value={pIndustry} onChange={e => setPreviewIndustry(e.target.value)}
                className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-xs max-w-[16rem]">
                {visible.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <PreviewFrame note="step 1">
              <div className="bg-grayscale-200 rounded-lg p-3">
                <OnboardingModal config={previewConfig} entry="first_run" plan="explorer" onFinish={() => undefined}
                  preview={{}} />
              </div>
            </PreviewFrame>
            <PreviewFrame note={`step 2, first chip picked — ${pIndustry || 'no industry'}`}>
              <div className="bg-grayscale-200 rounded-lg p-3">
                <OnboardingModal key={pIndustry} config={previewConfig} entry="first_run" plan="explorer"
                  onFinish={() => undefined} preview={{ industry: pIndustry, keyword: chipsOf(pIndustry)[0] || '' }} />
              </div>
            </PreviewFrame>
            <PreviewFrame note="Cosmos landing headings">
              <div className="flex flex-col gap-3">
                {[
                  { eyebrow: effectiveCopy.picks_eyebrow, heading: fillPicksHeading(effectiveCopy.picks_heading_kw, pIndustry, chipsOf(pIndustry)[0] || 'Keyword') },
                  { eyebrow: effectiveCopy.picks_eyebrow, heading: fillPicksHeading(effectiveCopy.picks_heading_industry, pIndustry) },
                  { eyebrow: effectiveCopy.picks_eyebrow_random, heading: fillPicksHeading(effectiveCopy.picks_heading_random) },
                ].map((h, i) => (
                  <div key={i}>
                    <div className="text-[11px] font-bold tracking-widest text-primary-700 uppercase">{h.eyebrow}</div>
                    {h.heading && <div className="text-lg font-bold text-grayscale-1300 font-grotesk">{h.heading}</div>}
                  </div>
                ))}
              </div>
            </PreviewFrame>
          </div>
        </div>
      </Section>

      <div className="flex items-center gap-2 flex-wrap sticky bottom-0 bg-grayscale-100/90 py-2">
        <Btn kind="primary" onClick={save} disabled={!dirty || !!busy}>{busy === 'save' ? 'Saving…' : 'Save'}</Btn>
        {dirty && !busy && <Btn onClick={discard}>Discard changes</Btn>}
        {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
        {data.config_version && <span className="text-[11px] text-grayscale-1100 ml-auto">config {data.config_version}</span>}
      </div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
    </div>
  );
};

export default Settings;
